<script setup>
import { ref, computed, watch } from 'vue'

// --- Icons (Inline SVGs for portability) ---
const IconCalendar = {
  template: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="4" rx="2" ry="2"/><line x1="16" x2="16" y1="2" y2="6"/><line x1="8" x2="8" y1="2" y2="6"/><line x1="3" x2="21" y1="10" y2="10"/></svg>`
}
const IconChevronDown = {
  template: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg>`
}
const IconArrowRight = {
  template: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>`
}
const IconChevronLeft = {
  template: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg>`
}
const IconChevronRight = {
  template: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>`
}

// --- Constants & Config ---
// days = difference in days between start and end
const presets = [
  { label: 'Last 24 Hours', days: 1 },
  { label: 'Last 7 Days', days: 7 },
  { label: 'Last 14 Days', days: 14 },
  { label: 'Last 30 Days', days: 30 },
  { label: 'Last 90 Days', days: 90 }
]

// --- Helpers ---
const formatDate = date => {
  if (!date) return ''
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

const formatFullDate = date => {
  if (!date) return ''
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

// Normalize a date to midnight (00:00:00) to ensure accurate day-based comparisons
const normalizeDate = d => {
  const date = new Date(d)
  date.setHours(0, 0, 0, 0)
  return date
}

const isSameDay = (d1, d2) => {
  if (!d1 || !d2) return false
  return d1.getDate() === d2.getDate() && d1.getMonth() === d2.getMonth() && d1.getFullYear() === d2.getFullYear()
}

const isDateBetween = (date, start, end) => {
  if (!start || !end || !date) return false
  const d = normalizeDate(date).getTime()
  const s = normalizeDate(start).getTime()
  const e = normalizeDate(end).getTime()
  return d > Math.min(s, e) && d < Math.max(s, e)
}

// --- State ---
const isOpen = ref(false)
const activeTab = ref('presets') // 'presets' | 'absolute'

// Default Value (Last 14 days)
const defaultEnd = normalizeDate(new Date())
const defaultStart = normalizeDate(new Date())
defaultStart.setDate(defaultEnd.getDate() - 14)

const modelValue = ref({
  type: 'relative',
  label: 'Last 14 Days',
  start: defaultStart,
  end: defaultEnd
})

// Temporary state for the Absolute Calendar view
const currentMonth = ref(new Date())
const tempStart = ref(null)
const tempEnd = ref(null)
const hoverDate = ref(null)

// --- Calendar Logic ---
const daysInMonth = computed(() => {
  const year = currentMonth.value.getFullYear()
  const month = currentMonth.value.getMonth()
  const date = new Date(year, month, 1)
  const days = []

  const firstDayIndex = date.getDay() // 0 = Sunday
  for (let i = 0; i < firstDayIndex; i++) {
    days.push(null)
  }

  while (date.getMonth() === month) {
    days.push(new Date(date))
    date.setDate(date.getDate() + 1)
  }
  return days
})

const monthName = computed(() => {
  return currentMonth.value.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
})

const nextMonth = () => {
  currentMonth.value = new Date(currentMonth.value.getFullYear(), currentMonth.value.getMonth() + 1, 1)
}

const prevMonth = () => {
  currentMonth.value = new Date(currentMonth.value.getFullYear(), currentMonth.value.getMonth() - 1, 1)
}

// --- Actions ---

const syncTempState = () => {
  // Always pre-fill calendar with current modelValue, even if it's a preset
  if (modelValue.value.start) {
    tempStart.value = new Date(modelValue.value.start)
    currentMonth.value = new Date(modelValue.value.start)
  }
  if (modelValue.value.end) {
    tempEnd.value = new Date(modelValue.value.end)
  }
}

const toggleDropdown = () => {
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    syncTempState()
  } else {
    // Reset temp state when closing (optional, mostly for cleanup)
    tempStart.value = null
    tempEnd.value = null
  }
}

const switchToAbsolute = () => {
  activeTab.value = 'absolute'
  syncTempState()
}

const selectPreset = (days, label) => {
  const end = normalizeDate(new Date())
  const start = normalizeDate(new Date())
  start.setDate(end.getDate() - days)

  modelValue.value = {
    type: 'relative',
    label: label,
    start,
    end
  }
  isOpen.value = false
}

const handleDayClick = date => {
  const selected = normalizeDate(date)

  if (!tempStart.value || (tempStart.value && tempEnd.value)) {
    // Start new selection
    tempStart.value = selected
    tempEnd.value = null
  } else {
    // Complete selection
    if (selected < tempStart.value) {
      tempEnd.value = tempStart.value
      tempStart.value = selected
    } else {
      tempEnd.value = selected
    }
  }
}

const applyAbsoluteDate = () => {
  if (!tempStart.value || !tempEnd.value) return

  const s = normalizeDate(tempStart.value).getTime()
  const e = normalizeDate(tempEnd.value).getTime()
  const today = normalizeDate(new Date()).getTime()

  // Calculate difference in days
  const diffTime = Math.abs(e - s)
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24))

  // Check if range ends today (presets are usually relative to today)
  const isEndToday = e === today

  // Try to find a matching preset
  let matchedPreset = null
  if (isEndToday) {
    matchedPreset = presets.find(p => p.days === diffDays)
  }

  if (matchedPreset) {
    // Auto-select the preset if the range matches exactly
    modelValue.value = {
      type: 'relative',
      label: matchedPreset.label,
      start: tempStart.value,
      end: tempEnd.value
    }
  } else {
    // Otherwise use absolute custom range
    modelValue.value = {
      type: 'absolute',
      label: `${formatDate(tempStart.value)} - ${formatDate(tempEnd.value)}`,
      start: tempStart.value,
      end: tempEnd.value
    }
  }

  isOpen.value = false
}
</script>

<template>
  <div class="relative w-full max-w-sm font-sans text-slate-700">
    <!-- Trigger Button -->
    <button
      class="flex items-center justify-between w-full px-3 py-2 bg-white border border-slate-300 rounded hover:bg-slate-50 hover:border-slate-400 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
      :class="{ 'ring-2 ring-purple-100 border-purple-500': isOpen }"
      @click="toggleDropdown"
    >
      <div class="flex items-center gap-2">
        <component
          :is="IconCalendar"
          class="w-4 h-4 text-slate-500"
        />
        <span class="text-sm font-medium">{{ modelValue.label }}</span>
      </div>
      <component
        :is="IconChevronDown"
        class="w-4 h-4 text-slate-400"
      />
    </button>

    <!-- Backdrop -->
    <div
      v-if="isOpen"
      class="fixed inset-0 z-40 bg-transparent"
      @click="isOpen = false"
    ></div>

    <!-- Dropdown Panel -->
    <div
      v-if="isOpen"
      class="absolute left-0 z-50 mt-2 bg-white border border-slate-200 rounded-lg shadow-xl w-[480px] overflow-hidden flex flex-col md:flex-row animate-in fade-in zoom-in-95 duration-100"
    >
      <!-- Sidebar (Presets) -->
      <div class="w-full md:w-40 bg-slate-50 border-r border-slate-100 flex-shrink-0 flex flex-col">
        <div class="p-2 space-y-1">
          <div class="px-2 py-1 text-xs font-semibold text-slate-400 uppercase tracking-wider">Presets</div>
          <button
            v-for="preset in presets"
            :key="preset.label"
            class="w-full text-left px-3 py-1.5 text-sm rounded-md transition-colors"
            :class="
              modelValue.label === preset.label
                ? 'bg-purple-100 text-purple-700 font-medium'
                : 'hover:bg-slate-200 text-slate-600'
            "
            @click="selectPreset(preset.days, preset.label)"
          >
            {{ preset.label }}
          </button>
        </div>

        <div class="mt-auto p-2 border-t border-slate-200">
          <button
            class="w-full text-left px-3 py-1.5 text-sm rounded-md transition-colors flex items-center justify-between"
            :class="
              activeTab === 'absolute'
                ? 'bg-white shadow-sm text-purple-700 font-medium ring-1 ring-slate-200'
                : 'hover:bg-slate-200 text-slate-600'
            "
            @click="switchToAbsolute"
          >
            <span>Absolute Date</span>
            <component
              :is="IconArrowRight"
              class="w-3 h-3"
            />
          </button>
        </div>
      </div>

      <!-- Main Content Area -->
      <div
        v-if="activeTab === 'absolute'"
        class="flex-1 p-4"
      >
        <!-- Header / Navigation -->
        <div class="flex items-center justify-between mb-4">
          <button
            class="p-1 hover:bg-slate-100 rounded-full text-slate-500"
            @click="prevMonth"
          >
            <component
              :is="IconChevronLeft"
              class="w-5 h-5"
            />
          </button>
          <span class="font-semibold text-slate-700">{{ monthName }}</span>
          <button
            class="p-1 hover:bg-slate-100 rounded-full text-slate-500"
            @click="nextMonth"
          >
            <component
              :is="IconChevronRight"
              class="w-5 h-5"
            />
          </button>
        </div>

        <!-- Days Grid -->
        <div class="grid grid-cols-7 gap-1 mb-2">
          <div
            v-for="day in ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']"
            :key="day"
            class="text-center text-xs font-medium text-slate-400 py-1"
          >
            {{ day }}
          </div>
        </div>

        <div class="grid grid-cols-7 gap-1">
          <div
            v-for="(date, index) in daysInMonth"
            :key="index"
            class="relative"
          >
            <template v-if="date">
              <button
                class="w-8 h-8 mx-auto flex items-center justify-center text-sm rounded-full transition-all relative z-10"
                :class="[
                  // Selected Start or End
                  isSameDay(date, tempStart) || isSameDay(date, tempEnd)
                    ? 'bg-purple-600 text-white font-bold shadow-md hover:bg-purple-700'
                    : 'text-slate-700 hover:bg-slate-100',

                  // Today marker
                  isSameDay(date, new Date()) && !isSameDay(date, tempStart) && !isSameDay(date, tempEnd)
                    ? 'text-purple-600 font-bold bg-purple-50'
                    : ''
                ]"
                @click="handleDayClick(date)"
                @mouseenter="hoverDate = date"
                @mouseleave="hoverDate = null"
              >
                {{ date.getDate() }}
              </button>

              <!-- Range Background connector -->
              <div
                v-if="
                  isDateBetween(date, tempStart, tempEnd) ||
                  (tempStart && !tempEnd && isDateBetween(date, tempStart, hoverDate))
                "
                class="absolute top-0 bottom-0 left-[-2px] right-[-2px] bg-purple-100 z-0"
              ></div>
            </template>
          </div>
        </div>

        <!-- Footer -->
        <div class="mt-4 pt-4 border-t border-slate-100 flex flex-col gap-2">
          <div class="text-xs text-slate-500 text-center h-4">
            <span v-if="tempStart && tempEnd">{{ formatFullDate(tempStart) }} - {{ formatFullDate(tempEnd) }}</span>
            <span v-else-if="tempStart">Select end date</span>
            <span v-else>Select a range</span>
          </div>
          <button
            :disabled="!tempStart || !tempEnd"
            class="w-full bg-purple-600 text-white text-sm font-medium py-2 rounded shadow-sm hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            @click="applyAbsoluteDate"
          >
            Apply Range
          </button>
        </div>
      </div>

      <!-- Placeholder for Presets view -->
      <div
        v-else
        class="flex-1 p-6 flex flex-col items-center justify-center text-slate-400 text-sm bg-white"
      >
        <component
          :is="IconCalendar"
          class="w-12 h-12 mb-2 text-slate-200"
        />
        <p>Select a preset from the sidebar</p>
        <p>or choose "Absolute Date"</p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.animate-in {
  animation-duration: 150ms;
  animation-fill-mode: forwards;
  animation-timing-function: cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes zoom-in-95 {
  from {
    transform: scale(0.95);
  }
  to {
    transform: scale(1);
  }
}
</style>
